import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemText,
    Divider,
    Badge,
    Snackbar,
    Alert
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

interface Notification {
    message: string;
    important?: boolean;
    timestamp?: Date;
}

const Footer: React.FC = () => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const [user, setUser] = useState<any | null>(null);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const parsedUser = storedUser ? JSON.parse(storedUser).user : null;
        setUser(parsedUser);

        const goodbyeMessages = [
            "Thanks for using NEO. We hope the rest of your day is wonderful!",
            "You’ve successfully logged out. Until next time, stay awesome. 🌟",
            "Goodbye for now! May your next login be swift and bug-free.",
            "Signing off with style. ✨",
            "You’re logged out, but your brilliance is still echoing through the servers.",
            "Your session ended, but your impact remains. 💪",
            "Logging out complete. Your productivity left a glow in our circuits.",
            "NEO is waving goodbye 👋 — we’ll be here when you get back.",
            "You’ve left the building... virtually. Take care!",
            "Have a productive rest of your day!",
            "Wheels up! You’re cleared for departure from the NEO zone. 🚀",
            "Thanks for powering today’s progress. NEO will hold the fort.",
            "Poof! You’re offline — but still awesome.",
            "Safe travels through cyberspace. Until we connect again!",
            "And just like that, you’ve vanished into the digital sunset. 🌇",
            "Session ended. Your data is tucked in safely for the night.",
            "It’s not goodbye, it’s just ‘see you later’. Thanks for stopping by!",
        ];

        const wasLoggedOut = localStorage.getItem('logoutNotificationShown');

        if (!parsedUser && !wasLoggedOut) {
            const randomMessage = goodbyeMessages[Math.floor(Math.random() * goodbyeMessages.length)];
            pushNotification(randomMessage, true);
            localStorage.setItem('logoutNotificationShown', 'true');
        } else if (parsedUser) {
            localStorage.removeItem('logoutNotificationShown');
        }

    }, []);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setUnreadCount(0);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const pushNotification = (message: string, important = false) => {
        const newNotification: Notification = {
            message,
            important,
            timestamp: new Date(),
        };
        setNotifications((prev) => [newNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);

        if (important) {
            setSnackbarMessage(message);
            setShowSnackbar(true);
        }
    };

    return (
        <>
            <Box
                component="footer"
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: 48,
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.secondary,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
                    zIndex: 1100,
                }}
            >
                <Typography variant="body2">
                    © {new Date().getFullYear()} Next Era Office. All rights reserved.
                </Typography>

                {user && (
                    <IconButton color="inherit" onClick={handleOpen} size="small">
                        <Badge badgeContent={unreadCount} color="error">
                            <NotificationsIcon />
                        </Badge>
                    </IconButton>
                )}
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                PaperProps={{
                    sx: {
                        width: 320,
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                    },
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontSize={16}>
                        Notifications
                    </Typography>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 1 }} />
                <List dense>
                    {notifications.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No notifications yet.
                        </Typography>
                    ) : (
                        notifications.map((note, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={note.message}
                                    secondary={note.timestamp?.toLocaleTimeString()}
                                    primaryTypographyProps={{ fontSize: 14 }}
                                />
                            </ListItem>
                        ))
                    )}
                </List>
            </Popover>

            <Snackbar
                open={showSnackbar}
                autoHideDuration={6000}
                onClose={() => setShowSnackbar(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity="info" variant="filled" onClose={() => setShowSnackbar(false)}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Footer;
